import React from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import CTAButton from '../ctaButton/CTAButton';
import Tooltip from '../tooltip/Tooltip';
import { parseLine } from '../../utils';

const CTAWrapper = ({ cta }) => {
  if (!cta) return null;
  return <CTAButton {...cta} />;
};

const icons = {
  Star: 'icon-star-01',
  Tick: 'icon-starling-tick',
  Question: 'icon-starling-question',
  Bullets: 'bullet-list-element',
  Parent: 'icon-convo-parent',
  Child: 'icon-convo-child',
  Smartphone: 'icon-smartphone',
  ID: 'icon-id',
  Documents: 'icon-documents',
  Time: 'icon-time-bca',
  One: 'icon-one',
  Two: 'icon-two',
  Three: 'icon-three',
  Four: 'icon-four',
  Five: 'icon-five'
};

export const Item = (props) => {
  const { text, markdownText, src, link, tooltip, icon, target } = props;

  if (tooltip) {
    return (
      <li className={classNames('list-item--tooltip', icon)}>
        {parseLine(text)}
        {tooltip && <Tooltip heading={text} bio={tooltip} />}
      </li>
    );
  } else if (link) {
    return (
      <li className={icon}>
        {target ? (
          // eslint-disable-next-line
          <a
            dangerouslySetInnerHTML={{ __html: markdownText ? markdownText : stripParagraphs(text) }}
            href={link}
            target={target}
          />
        ) : (
          // eslint-disable-next-line
          <a dangerouslySetInnerHTML={{ __html: markdownText ? markdownText : stripParagraphs(text) }} href={link} />
        )}
      </li>
    );
  } else if (src) {
    return (
      <li className={icon}>
        <div dangerouslySetInnerHTML={{ __html: props[src] }} />
      </li>
    );
  } else {
    return (
      <li
        className={classNames(icon, { 'md-text': markdownText })}
        dangerouslySetInnerHTML={{ __html: markdownText ? markdownText : stripParagraphs(text) }}
      />
    );
  }
};

const stripParagraphs = (text) => text.replace(/^<p>/, '').replace(/<\/p>\s*$/, '');

const List = ({
  title,
  bigTitle,
  titleAlignment,
  titleColour,
  intro,
  icon,
  items,
  footer,
  colourScheme,
  listType,
  split,
  callToAction,
  sectionClass,
  sectionId,
  children,
  customPadding,
  fontSize = 'medium',
  styleHeadingAs
}) => {
  const componentClasses = classNames('list-section', {
    [sectionClass]: sectionClass,
    [`list-section--${colourScheme}-scheme`]: colourScheme,
    [`list-section--font-size-${fontSize}`]: fontSize,
    [`titles--${titleColour}`]: titleColour,
    [`list-section-title--${titleAlignment}`]: titleAlignment,
    [`list-section--${listType}`]: listType,
    [`list-section--${split}-cols`]: split,
    'list-section--no-padding-top': customPadding === 'no-top',
    'list-section--no-padding-bottom': customPadding === 'no-bottom',
    'list-section--no-padding-top-bottom': customPadding === 'no-padding'
  });

  const listItems = items || children;
  const itemsWithIcon = React.Children.map(listItems, (item) =>
    React.cloneElement(item, { icon: icon ? icons[icon] : icons[item.props.icon] })
  );

  return (
    <section className={componentClasses} id={sectionId}>
      <div className="list-section__inner">
        {bigTitle && <h2 className={styleHeadingAs ? `as-${styleHeadingAs}` : ''}>{bigTitle}</h2>}
        {title && <h3 dangerouslySetInnerHTML={{ __html: stripParagraphs(title) }} />}
        {intro && <p dangerouslySetInnerHTML={{ __html: stripParagraphs(intro) }} />}
        <ul>{itemsWithIcon}</ul>
        {footer && <p className="list-section__footer" dangerouslySetInnerHTML={{ __html: stripParagraphs(footer) }} />}
      </div>
      <CTAWrapper cta={callToAction} />
    </section>
  );
};

List.Item = Item;

List.propTypes = {
  title: PropTypes.string,
  bigTitle: PropTypes.string,
  titleAlignment: PropTypes.oneOf(['center', 'left']),
  titleColour: PropTypes.oneOf(['purple', 'dark-navy', 'teal', 'navy']),
  intro: PropTypes.string,
  items: PropTypes.array,
  footer: PropTypes.string,
  colourScheme: PropTypes.oneOf(['grey', 'light-grey', 'dark-grey', 'dark-navy']),
  split: PropTypes.oneOf(['two', 'three']),
  sectionClass: PropTypes.string,
  sectionId: PropTypes.string,
  icon: PropTypes.oneOf(Object.keys(icons)),
  callToAction: PropTypes.shape(CTAButton.propTypes),
  listType: PropTypes.oneOf(['list-box', 'download-box', 'compressed', 'wide', 'full-width']),
  fontSize: PropTypes.oneOf(['small', 'medium']),
  customPadding: PropTypes.oneOf(['no-top', 'no-bottom', 'no-padding']),
  children: PropTypes.array,
  styleHeadingAs: PropTypes.oneOf(['h2', 'h3'])
};

export default List;
